/**
 * Garden Elements Module
 * Handles garden elements like compost piles, greenhouses, etc.
 */

// Configuration
const GRID_SIZE = 40; // Size of the background grid (should match CSS)
const GARDEN_SURFACE_STORAGE_KEY = 'garden_surface_preset';
const GARDEN_GRID_OVERLAY_STORAGE_KEY = 'garden_surface_grid_overlay';
const GARDEN_SURFACE_BASE_CLASSES = [
    'surface-grass',
    'surface-white',
    'surface-dirt',
    'surface-concrete'
];
const SURFACE_GRID_OVERLAY_CLASS = 'surface-grid-overlay';

let currentGardenSurfaceBase = 'grass';
let currentGardenGridOverlay = false;

// Track all garden elements placed in the garden
let gardenElementInstances = [];

// Handle starting drag of a garden element
function startElementDrag(e) {
    e.preventDefault();
    
    // Get the garden beds container
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    if (!gardenBedsContainer) {
        console.warn('Garden beds container not found');
        return;
    }
    
    // Create a new instance of the garden element
    const elementType = this.getAttribute('data-element');
    const elementName = this.querySelector('span').textContent;
    const elementImgSrc = this.querySelector('img').src;
    
    // Create the new element instance
    const elementInstance = document.createElement('div');
    elementInstance.className = 'garden-element-instance';
    elementInstance.setAttribute('data-element-type', elementType);
    elementInstance.setAttribute('data-element-id', Date.now().toString());
    
    // Set initial size based on element type (can be customized)
    let initialWidth = 100;
    let initialHeight = 100;
    
    // Adjust size for specific elements
    switch(elementType) {
        case 'fruit-tree':
            initialWidth = 120;
            initialHeight = 120;
            break;
        case 'greenhouse':
            initialWidth = 140;
            initialHeight = 100;
            break;
        case 'trellis':
            initialWidth = 80;
            initialHeight = 120;
            break;
        // Add more custom sizes as needed
    }
    
    elementInstance.style.width = `${initialWidth}px`;
    elementInstance.style.height = `${initialHeight}px`;
    
    // Create the image element
    const imgElement = document.createElement('img');
    imgElement.src = elementImgSrc;
    imgElement.alt = elementName;
    
    // Create controls
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'element-controls';
    
    // Delete button
    const deleteButton = document.createElement('button');
    deleteButton.className = 'element-control-btn delete-element-btn';
    deleteButton.innerHTML = '×';
    deleteButton.title = 'Delete element';
    deleteButton.addEventListener('click', (e) => {
        e.stopPropagation();
        
        elementInstance.remove();
        removeGardenElementFromStorage(elementInstance.getAttribute('data-element-id'));
    });
    
    // Rotate button
    const rotateButton = document.createElement('button');
    rotateButton.className = 'element-control-btn rotate-element-btn';
    rotateButton.innerHTML = '↻';
    rotateButton.title = 'Rotate element';
    rotateButton.addEventListener('click', (e) => {
        e.stopPropagation();
        rotateElement(elementInstance);
    });
    
    // Add duplicate button
    const duplicateButton = document.createElement('button');
    duplicateButton.className = 'element-control-btn duplicate-element-btn';
    duplicateButton.innerHTML = '⧉';
    duplicateButton.title = 'Duplicate element';
    duplicateButton.addEventListener('click', (e) => {
        e.stopPropagation();
        duplicateElement(elementInstance);
    });
    
    // Add buttons to controls
    controlsContainer.appendChild(deleteButton);
    controlsContainer.appendChild(rotateButton);
    controlsContainer.appendChild(duplicateButton);
    
    // Resize handle
    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'resize-element-handle';
    resizeHandle.innerHTML = '⤡';
    resizeHandle.addEventListener('mousedown', startResize);
    resizeHandle.addEventListener('touchstart', startResize, { passive: false });
    
    // Add all elements to the instance
    elementInstance.appendChild(imgElement);
    elementInstance.appendChild(controlsContainer);
    elementInstance.appendChild(resizeHandle);
    
    // Add to garden beds container
    gardenBedsContainer.appendChild(elementInstance);
    
    // Position at cursor
    const containerRect = gardenBedsContainer.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    let left = clientX - containerRect.left - (initialWidth / 2);
    let top = clientY - containerRect.top - (initialHeight / 2);
    
    // Apply position
    elementInstance.style.position = 'absolute';
    elementInstance.style.left = `${left}px`;
    elementInstance.style.top = `${top}px`;
    
    // Make the element instance draggable
    makeElementDraggable(elementInstance);
    
    // Add to tracked elements and save
    const elementData = {
        id: elementInstance.getAttribute('data-element-id'),
        type: elementType,
        name: elementName,
        imgSrc: elementImgSrc,
        position: { left, top },
        size: { width: initialWidth, height: initialHeight },
        rotation: 0
    };
    
    gardenElementInstances.push(elementData);
    saveGardenElements();
    
    // Start dragging the newly created element
    const mouseEvent = new MouseEvent('mousedown', {
        clientX: clientX,
        clientY: clientY,
        bubbles: true
    });
    elementInstance.dispatchEvent(mouseEvent);
}

// Make element instance draggable
function makeElementDraggable(element) {
    let offsetX, offsetY, isDragging = false;
    
    element.addEventListener('mousedown', startElementInstanceDrag);
    element.addEventListener('touchstart', startElementInstanceDrag, { passive: false });
    
    function startElementInstanceDrag(e) {
        e.preventDefault();
        
        // Ignore if clicked on a control
        if (e.target.closest('.element-control-btn, .resize-element-handle')) {
            return;
        }
        
        // Calculate initial offset
        const rect = element.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        
        offsetX = clientX - rect.left;
        offsetY = clientY - rect.top;
        
        isDragging = true;
        
        // Add global mouse/touch events
        document.addEventListener('mousemove', dragMove);
        document.addEventListener('touchmove', dragMove, { passive: false });
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchend', endDrag);
        
        // Add active dragging class
        element.classList.add('dragging');
    }
    
    function dragMove(e) {
        if (!isDragging) return;
        
        e.preventDefault();
        
        const container = document.getElementById('garden-beds-container');
        if (!container) return;
        
        const containerRect = container.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        
        // Calculate new position within container bounds
        let left = clientX - containerRect.left - offsetX;
        let top = clientY - containerRect.top - offsetY;
        
        // Apply boundary limits
        const maxLeft = containerRect.width - element.offsetWidth;
        const maxTop = containerRect.height - element.offsetHeight;
        
        left = Math.max(0, Math.min(left, maxLeft));
        top = Math.max(0, Math.min(top, maxTop));
        
        // Set new position
        element.style.left = `${left}px`;
        element.style.top = `${top}px`;
        
        // Update element data
        const elementId = element.getAttribute('data-element-id');
        updateElementPosition(elementId, left, top);
    }
    
    function endDrag() {
        if (!isDragging) return;
        
        isDragging = false;
        document.removeEventListener('mousemove', dragMove);
        document.removeEventListener('touchmove', dragMove);
        document.removeEventListener('mouseup', endDrag);
        document.removeEventListener('touchend', endDrag);
        element.classList.remove('dragging');
        
        // Save updated elements
        saveGardenElements();
    }
}

// Start resizing an element
function startResize(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const element = this.parentElement;
    const container = document.getElementById('garden-beds-container');
    
    if (!element || !container) return;
    
    const initialWidth = element.offsetWidth;
    const initialHeight = element.offsetHeight;
    const startX = e.clientX || (e.touches && e.touches[0].clientX);
    const startY = e.clientY || (e.touches && e.touches[0].clientY);
    
    let isResizing = true;
    element.classList.add('resizing');
    
    function resizeMove(e) {
        if (!isResizing) return;
        
        e.preventDefault();
        
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        
        const deltaX = clientX - startX;
        const deltaY = clientY - startY;
        
        // Calculate new size
        let newWidth = initialWidth + deltaX;
        let newHeight = initialHeight + deltaY;
        
        // Enforce minimum size
        newWidth = Math.max(40, newWidth);
        newHeight = Math.max(40, newHeight);
        
        // Apply new size
        element.style.width = `${newWidth}px`;
        element.style.height = `${newHeight}px`;
        
        // Update element data
        const elementId = element.getAttribute('data-element-id');
        updateElementSize(elementId, newWidth, newHeight);
    }
    
    function endResize() {
        if (!isResizing) return;
        
        isResizing = false;
        element.classList.remove('resizing');
        
        document.removeEventListener('mousemove', resizeMove);
        document.removeEventListener('touchmove', resizeMove);
        document.removeEventListener('mouseup', endResize);
        document.removeEventListener('touchend', endResize);
        
        // Save updated elements
        saveGardenElements();
    }
    
    document.addEventListener('mousemove', resizeMove);
    document.addEventListener('touchmove', resizeMove, { passive: false });
    document.addEventListener('mouseup', endResize);
    document.addEventListener('touchend', endResize);
}

// Rotate an element
function rotateElement(element) {
    // Get current rotation or default to 0
    let currentRotation = parseInt(element.getAttribute('data-rotation') || '0');
    
    // Increment rotation by 45 degrees
    currentRotation = (currentRotation + 45) % 360;
    
    // Apply rotation
    element.style.transform = `rotate(${currentRotation}deg)`;
    element.setAttribute('data-rotation', currentRotation.toString());
    
    // Update element data
    const elementId = element.getAttribute('data-element-id');
    updateElementRotation(elementId, currentRotation);
    
    // Save updated elements
    saveGardenElements();
}

// Update element position in data
function updateElementPosition(elementId, left, top) {
    const elementIndex = gardenElementInstances.findIndex(elem => elem.id === elementId);
    if (elementIndex !== -1) {
        gardenElementInstances[elementIndex].position = { left, top };
    }
}

// Update element size in data
function updateElementSize(elementId, width, height) {
    const elementIndex = gardenElementInstances.findIndex(elem => elem.id === elementId);
    if (elementIndex !== -1) {
        gardenElementInstances[elementIndex].size = { width, height };
    }
}

// Update element rotation in data
function updateElementRotation(elementId, rotation) {
    const elementIndex = gardenElementInstances.findIndex(elem => elem.id === elementId);
    if (elementIndex !== -1) {
        gardenElementInstances[elementIndex].rotation = rotation;
    }
}

// Save garden elements to localStorage
function saveGardenElements() {
    try {
        localStorage.setItem('garden_elements', JSON.stringify(gardenElementInstances));
    } catch (error) {
        console.error('Error saving garden elements:', error);
    }
}

function applyGardenSurfaceState(basePreset = 'grass', showGridOverlay = false) {
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    if (!gardenBedsContainer) return;

    const base = ['grass', 'white', 'dirt', 'concrete'].includes(basePreset) ? basePreset : 'grass';
    currentGardenSurfaceBase = base;
    currentGardenGridOverlay = Boolean(showGridOverlay);

    gardenBedsContainer.classList.remove(...GARDEN_SURFACE_BASE_CLASSES, SURFACE_GRID_OVERLAY_CLASS);
    gardenBedsContainer.classList.add(`surface-${base}`);
    if (currentGardenGridOverlay) {
        gardenBedsContainer.classList.add(SURFACE_GRID_OVERLAY_CLASS);
    }

    const presetButtons = document.querySelectorAll('.garden-surface-preset');
    presetButtons.forEach(button => {
        const preset = button.dataset.surfacePreset;
        if (preset === 'grid') {
            button.classList.toggle('active', currentGardenGridOverlay);
            button.setAttribute('aria-pressed', String(currentGardenGridOverlay));
        } else {
            const isActive = preset === base;
            button.classList.toggle('active', isActive);
            button.setAttribute('aria-pressed', String(isActive));
        }
    });

    try {
        localStorage.setItem(GARDEN_SURFACE_STORAGE_KEY, base);
        localStorage.setItem(GARDEN_GRID_OVERLAY_STORAGE_KEY, String(currentGardenGridOverlay));
    } catch (error) {
        console.error('Error saving garden surface preset:', error);
    }
}

function loadSavedGardenSurfacePreset() {
    let base = 'grass';
    let gridOverlay = false;

    try {
        const saved = localStorage.getItem(GARDEN_SURFACE_STORAGE_KEY) || 'grass';
        if (saved === 'grid') {
            base = 'white';
            gridOverlay = true;
            localStorage.setItem(GARDEN_SURFACE_STORAGE_KEY, 'white');
            localStorage.setItem(GARDEN_GRID_OVERLAY_STORAGE_KEY, 'true');
        } else if (['grass', 'white', 'dirt', 'concrete'].includes(saved)) {
            base = saved;
        }
        const gridStored = localStorage.getItem(GARDEN_GRID_OVERLAY_STORAGE_KEY);
        if (gridStored !== null) {
            gridOverlay = gridStored === 'true';
        }
    } catch (error) {
        console.error('Error loading garden surface preset:', error);
    }

    applyGardenSurfaceState(base, gridOverlay);
}

function setupGardenSurfacePresetListeners() {
    const presetButtons = document.querySelectorAll('.garden-surface-preset');

    presetButtons.forEach(button => {
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            const preset = button.dataset.surfacePreset;
            if (preset === 'grid') {
                applyGardenSurfaceState(currentGardenSurfaceBase, !currentGardenGridOverlay);
            } else {
                applyGardenSurfaceState(preset, currentGardenGridOverlay);
            }
        });
    });
}

// Load saved garden elements
function loadSavedGardenElements() {
    const storedElements = localStorage.getItem('garden_elements');
    
    if (storedElements) {
        try {
            gardenElementInstances = JSON.parse(storedElements);
            
            // Recreate each element
            gardenElementInstances.forEach(element => {
                createGardenElementFromData(element);
            });
        } catch (error) {
            console.error('Error loading saved garden elements:', error);
            gardenElementInstances = [];
        }
    }
}

// Create garden element from saved data
function createGardenElementFromData(elementData) {
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    if (!gardenBedsContainer) return;
    
    // Create the element instance
    const elementInstance = document.createElement('div');
    elementInstance.className = 'garden-element-instance';
    elementInstance.setAttribute('data-element-type', elementData.type);
    elementInstance.setAttribute('data-element-id', elementData.id);
    
    // Apply saved position and size
    elementInstance.style.position = 'absolute';
    elementInstance.style.left = `${elementData.position.left}px`;
    elementInstance.style.top = `${elementData.position.top}px`;
    elementInstance.style.width = `${elementData.size.width}px`;
    elementInstance.style.height = `${elementData.size.height}px`;
    
    // Apply rotation if any
    if (elementData.rotation) {
        elementInstance.style.transform = `rotate(${elementData.rotation}deg)`;
        elementInstance.setAttribute('data-rotation', elementData.rotation.toString());
    }
    
    // Create the image element
    const imgElement = document.createElement('img');
    imgElement.src = elementData.imgSrc;
    imgElement.alt = elementData.name;
    
    // Create controls
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'element-controls';
    
    // Delete button
    const deleteButton = document.createElement('button');
    deleteButton.className = 'element-control-btn delete-element-btn';
    deleteButton.innerHTML = '×';
    deleteButton.title = 'Delete element';
    deleteButton.addEventListener('click', (e) => {
        e.stopPropagation();
        elementInstance.remove();
        removeGardenElementFromStorage(elementData.id);
    });
    
    // Rotate button
    const rotateButton = document.createElement('button');
    rotateButton.className = 'element-control-btn rotate-element-btn';
    rotateButton.innerHTML = '↻';
    rotateButton.title = 'Rotate element';
    rotateButton.addEventListener('click', (e) => {
        e.stopPropagation();
        rotateElement(elementInstance);
    });
    
    // Add duplicate button
    const duplicateButton = document.createElement('button');
    duplicateButton.className = 'element-control-btn duplicate-element-btn';
    duplicateButton.innerHTML = '⧉';
    duplicateButton.title = 'Duplicate element';
    duplicateButton.addEventListener('click', (e) => {
        e.stopPropagation();
        duplicateElement(elementInstance);
    });
    
    // Add buttons to controls
    controlsContainer.appendChild(deleteButton);
    controlsContainer.appendChild(rotateButton);
    controlsContainer.appendChild(duplicateButton);
    
    // Resize handle
    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'resize-element-handle';
    resizeHandle.innerHTML = '⤡';
    resizeHandle.addEventListener('mousedown', startResize);
    resizeHandle.addEventListener('touchstart', startResize, { passive: false });
    
    // Add all elements to the instance
    elementInstance.appendChild(imgElement);
    elementInstance.appendChild(controlsContainer);
    elementInstance.appendChild(resizeHandle);
    
    // Add to garden beds container
    gardenBedsContainer.appendChild(elementInstance);
    
    // Make draggable
    makeElementDraggable(elementInstance);
}

// Remove garden element from storage
function removeGardenElementFromStorage(elementId) {
    gardenElementInstances = gardenElementInstances.filter(elem => elem.id !== elementId);
    saveGardenElements();
}

// Function to clear all garden elements
function clearAllGardenElements() {
    // Remove all element instances from the DOM
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    if (gardenBedsContainer) {
        const elementInstances = gardenBedsContainer.querySelectorAll('.garden-element-instance');
        elementInstances.forEach(element => {
            element.remove();
        });
    }
    
    // Clear data and storage
    gardenElementInstances = [];
    localStorage.removeItem('garden_elements');
}

// Connect to the existing "Clear All Layouts" button to also clear elements
function connectClearAllElementsButton() {
    const clearAllButton = document.getElementById('clear-all-layouts');
    if (clearAllButton) {
        // Add event listener that just clears elements without a separate confirmation
        clearAllButton.addEventListener('click', () => {
            clearAllGardenElements();
        });
    }
}

// Setup event listeners for garden elements
function setupGardenElementListeners() {
    // Make garden elements draggable
    const gardenElements = document.querySelectorAll('.garden-element');
    
    gardenElements.forEach(element => {
        element.addEventListener('mousedown', startElementDrag);
        element.addEventListener('touchstart', startElementDrag, { passive: false });
    });
}

// Main initialization function
export function initializeGardenElements() {
    // Setup event listeners
    setupGardenElementListeners();
    setupGardenSurfacePresetListeners();
    
    // Connect clear all button
    connectClearAllElementsButton();
    
    // Restore the selected background preset
    loadSavedGardenSurfacePreset();

    // Load any previously saved garden elements
    loadSavedGardenElements();
    
    console.log('✅ Garden elements functionality initialized');
}

// Export additional functions that might be needed by other modules
export {
    clearAllGardenElements,
    loadSavedGardenElements,
    saveGardenElements
};

// Function to duplicate a garden element
function duplicateElement(element) {
    const elementId = element.getAttribute('data-element-id');
    const elementData = gardenElementInstances.find(elem => elem.id === elementId);
    
    if (elementData) {
        // Create a new element data object with a new ID
        const newElementData = {
            ...elementData,
            id: Date.now().toString(),
            name: `${elementData.name} (Copy)`,
            position: {
                left: elementData.position.left + 20,
                top: elementData.position.top + 20
            }
        };
        
        // Add the new element to storage
        gardenElementInstances.push(newElementData);
        saveGardenElements();
        
        // Create and add the new element instance
        createGardenElementFromData(newElementData);
    }
} 